'use client';

import React, { createContext, use, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { createA2UISurfaceSnapshot, getBooleanAnyOfDerivedUpdates, setPathValue, valuesEqual, walkPath, type A2UISurfaceSnapshot, type RecordAny, type RenderComponent, type SurfaceComponent } from '@macaron-a2ui/core/v0_8';

export type MacaronA2UIActionHandler = (actionName: string, sourceComponentId: string, context?: Record<string, unknown> | null) => void;
export type MacaronA2UILocalHandler = (operation: string, sourceComponentId: string, value?: unknown) => void;

export interface MacaronA2UIState {
  root: string;
  components: SurfaceComponent[];
  data: RecordAny;
}

export interface MacaronA2UIActions {
  updateData: (path: string, value: unknown) => void;
  dispatchAction: MacaronA2UIActionHandler;
  dispatchLocalHandler: MacaronA2UILocalHandler;
}

export interface MacaronA2UIMeta {
  renderComponent: RenderComponent;
}

export interface MacaronA2UIContextValue {
  state: MacaronA2UIState;
  actions: MacaronA2UIActions;
  meta: MacaronA2UIMeta;
}

const EMPTY_RENDER_COMPONENT: RenderComponent = (type) => `Unsupported A2UI component: ${type}`;
const EMPTY_STATE: MacaronA2UIState = { root: 'root', components: [], data: {} };
const EMPTY_ACTIONS: MacaronA2UIActions = { updateData: () => {}, dispatchAction: () => {}, dispatchLocalHandler: () => {} };
const EMPTY_META: MacaronA2UIMeta = { renderComponent: EMPTY_RENDER_COMPONENT };
const MacaronA2UIContext = createContext<MacaronA2UIContextValue>({ state: EMPTY_STATE, actions: EMPTY_ACTIONS, meta: EMPTY_META });

export function useMacaronA2UIState() {
  return use(MacaronA2UIContext).state;
}

export function useMacaronA2UIActions() {
  return use(MacaronA2UIContext).actions;
}

export function useMacaronA2UIMeta() {
  return use(MacaronA2UIContext).meta;
}

export function useMacaronA2UIRenderComponent() {
  return useMacaronA2UIMeta().renderComponent;
}

function useSurfaceState(snapshot: A2UISurfaceSnapshot, onAction?: MacaronA2UIActionHandler, onLocalHandler?: MacaronA2UILocalHandler) {
  const [data, setData] = useState<RecordAny>(() => snapshot.data);
  useEffect(() => {
    setData((current) => (valuesEqual(current, snapshot.data) ? current : snapshot.data));
  }, [snapshot.data]);
  useEffect(() => {
    const updates = getBooleanAnyOfDerivedUpdates(snapshot.components, data);
    if (updates.length === 0) return;
    setData((current) => {
      let next = current;
      for (const update of updates) {
        if (valuesEqual(walkPath(update.path, next), update.value)) continue;
        next = setPathValue(next, update.path, update.value);
      }
      return next;
    });
  }, [snapshot.components, data]);
  const updateData = useCallback((path: string, value: unknown) => {
    if (!path.trim()) return;
    setData((current) => {
      const currentValue = walkPath(path, current);
      if (valuesEqual(currentValue, value)) return current;
      return setPathValue(current, path, value);
    });
  }, []);
  const dispatchAction = useCallback<MacaronA2UIActionHandler>((actionName, sourceId, context) => onAction?.(actionName, sourceId, context), [onAction]);
  const dispatchLocalHandler = useCallback<MacaronA2UILocalHandler>((operation, sourceId, value) => onLocalHandler?.(operation, sourceId, value), [onLocalHandler]);
  return useMemo(
    () => ({ state: { root: snapshot.root, components: snapshot.components, data }, actions: { updateData, dispatchAction, dispatchLocalHandler } }),
    [data, dispatchAction, dispatchLocalHandler, snapshot.components, snapshot.root, updateData],
  );
}

export interface MacaronA2UIProviderProps {
  state?: MacaronA2UIState;
  actions?: MacaronA2UIActions;
  meta?: MacaronA2UIMeta;
  renderComponent?: RenderComponent;
  children: ReactNode;
}

export function MacaronA2UIProvider({ state = EMPTY_STATE, actions = EMPTY_ACTIONS, meta, renderComponent, children }: MacaronA2UIProviderProps) {
  const resolvedMeta = useMemo(() => meta || { renderComponent: renderComponent || EMPTY_RENDER_COMPONENT }, [meta, renderComponent]);
  const value = useMemo(() => ({ state, actions, meta: resolvedMeta }), [actions, resolvedMeta, state]);
  return <MacaronA2UIContext.Provider value={value}>{children}</MacaronA2UIContext.Provider>;
}

export interface MacaronA2UISnapshotProviderProps {
  payload?: Record<string, unknown>;
  messages?: Record<string, unknown>[];
  renderComponent?: RenderComponent;
  onAction?: MacaronA2UIActionHandler;
  onLocalHandler?: MacaronA2UILocalHandler;
  children: ReactNode;
}

export function MacaronA2UISnapshotProvider({ payload, messages, renderComponent = EMPTY_RENDER_COMPONENT, onAction, onLocalHandler, children }: MacaronA2UISnapshotProviderProps) {
  const snapshot = useMemo(() => createA2UISurfaceSnapshot(payload, messages), [payload, messages]);
  const { state, actions } = useSurfaceState(snapshot, onAction, onLocalHandler);
  const meta = useMemo(() => ({ renderComponent }), [renderComponent]);
  return (
    <MacaronA2UIProvider state={state} actions={actions} meta={meta}>
      {children}
    </MacaronA2UIProvider>
  );
}

export { MacaronA2UIContext };
